import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG =
    "https://images.unsplash.com/photo-1642522029686-5485ea7e6042?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxsYXclMjBmaXJtJTIwb2ZmaWNlJTIwZGVza3xlbnwwfHx8fDE3OTAyMTYzODh8MA&ixlib=rb-4.1.0&q=85";

const TRUST = ["Integrity", "Confidentiality", "Professionalism", "Personal Attention"];

const MaskedLine = ({ children, delay, className }) => (
    <span className="block overflow-hidden">
        <motion.span
            className={`block ${className}`}
            initial={{ y: "112%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

    return (
        <section id="home" ref={ref} className="relative min-h-screen flex flex-col bg-[#0B192C] overflow-hidden">
            <motion.div className="absolute inset-0" style={{ y: bgY }}>
                <img
                    src={HERO_IMG}
                    alt="Dan Rosengarten Law Firm executive office workspace"
                    className="w-full h-[115%] object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B192C]/95 via-[#0B192C]/80 to-[#0B192C]/55" />
            </motion.div>

            <div className="relative max-w-6xl mx-auto px-6 lg:px-10 w-full flex-1 flex flex-col justify-center pt-36 pb-20">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.15 }}
                    className="font-mono-label text-[11px] tracking-[0.3em] uppercase text-[#DFCA9B] mb-8"
                    data-testid="hero-eyebrow"
                >
                    South African Legal Practice
                </motion.p>

                <h1 className="font-serif-display font-medium text-white text-4xl sm:text-5xl lg:text-[64px] leading-[1.1] tracking-tight">
                    <MaskedLine delay={0.3}>Dan Rosengarten</MaskedLine>
                    <MaskedLine delay={0.45} className="text-[#DFCA9B]">
                        Law Firm
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-6 font-serif-display italic text-xl sm:text-2xl text-white/85"
                    data-testid="hero-tagline"
                >
                    Experienced Legal Counsel. Personal Attention.
                </motion.p>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-6 max-w-xl text-base text-white/65 leading-relaxed"
                    data-testid="hero-intro"
                >
                    The firm provides professional legal representation, working closely with
                    each client to understand their individual legal needs and to protect their
                    interests with care, diligence, and discretion.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-10 flex flex-col sm:flex-row gap-4"
                >
                    <button
                        data-testid="hero-cta-schedule"
                        onClick={() => scrollToSection("contact")}
                        className="bg-[#B89047] text-[#0B192C] text-sm font-semibold tracking-wide px-8 py-4 hover:bg-[#DFCA9B] transition-colors duration-300 cursor-pointer"
                    >
                        Schedule a Consultation
                    </button>
                    <button
                        data-testid="hero-cta-contact"
                        onClick={() => scrollToSection("contact")}
                        className="border border-white/30 text-white text-sm font-semibold tracking-wide px-8 py-4 hover:border-[#B89047] hover:text-[#DFCA9B] transition-colors duration-300 cursor-pointer"
                    >
                        Contact the Firm
                    </button>
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.2, delay: 1.4 }}
                className="relative border-t border-white/10"
                data-testid="hero-trust-bar"
            >
                <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4">
                    {TRUST.map((t, i) => (
                        <div
                            key={t}
                            className={`py-6 md:py-7 text-center ${i > 0 ? "border-l border-white/10" : ""}`}
                        >
                            <span className="font-mono-label text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-[#DFCA9B]/90">
                                {t}
                            </span>
                        </div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
};

export default Hero;
